/**
 * Wizard Template Builder
 * Phase 3 - Let consultants create custom wizards
 * 
 * Features:
 * - Drag & drop questions from question bank
 * - Add/remove/reorder pages
 * - Configure wizard settings
 * - Configure placement (auto-launch, manual access)
 * - Publish/unpublish wizards
 */

import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { 
  Plus, Trash2, GripVertical, Save, Eye, EyeOff, ChevronDown, ChevronUp,
  Wand2, Settings, Layout, Zap, X, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { projectId, publicAnonKey } from '../../utils/supabase/info';
import { QuestionEditor } from '../question-bank/QuestionEditor';
import type { Question as QuestionBankQuestion } from '../../utils/api/questionBankApi';

interface Question {
  id: string;
  text: string;
  type: string;
  category: string;
  required: boolean;
  critical: boolean;
  grouping: {
    isPartOfWizard: boolean;
    wizardId?: string;
    gridBehavior: string;
  };
}

interface WizardPage {
  id: string;
  title: string;
  description: string;
  questionIds: string[];
}

interface WizardPlacement {
  autoLaunch: {
    enabled: boolean;
    trigger: 'on_create' | 'on_first_view' | 'never';
    categories: string[];
  };
  manualAccess: {
    showInCategoryActions: boolean;
    showInGridActions: boolean;
    showInCIDetail: boolean;
    showInWizardLibrary: boolean;
  };
  availableToRoles: string[];
  requiresConfigItem: boolean;
  allowMultipleRuns: boolean;
}

interface WizardTemplate {
  id?: string;
  name: string;
  description: string;
  category: string;
  pages: WizardPage[];
  allowSkip: boolean;
  showProgress: boolean;
  autoSave: boolean;
  published: boolean;
  placement: WizardPlacement;
  isDefault?: boolean;
  createdAt?: string;
}

interface WizardTemplateBuilderProps {
  open: boolean;
  onClose: () => void;
  wizardId?: string; // If editing existing wizard
  consultingFirmId: string;
  accessToken: string;
  onSave?: (wizard: WizardTemplate) => void;
}

export function WizardTemplateBuilder({
  open,
  onClose,
  wizardId,
  consultingFirmId,
  accessToken,
  onSave
}: WizardTemplateBuilderProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<'pages' | 'settings' | 'placement'>('pages');
  
  // Wizard state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('service_requests');
  const [pages, setPages] = useState<WizardPage[]>([
    { id: 'page_1', title: 'Page 1', description: '', questionIds: [] }
  ]);
  const [allowSkip, setAllowSkip] = useState(true);
  const [showProgress, setShowProgress] = useState(true);
  const [autoSave, setAutoSave] = useState(true);
  const [published, setPublished] = useState(false);
  const [placement, setPlacement] = useState<WizardPlacement>({
    autoLaunch: {
      enabled: false,
      trigger: 'never',
      categories: []
    },
    manualAccess: {
      showInCategoryActions: true,
      showInGridActions: true,
      showInCIDetail: true,
      showInWizardLibrary: true
    },
    availableToRoles: ['both'],
    requiresConfigItem: true,
    allowMultipleRuns: true
  });

  // Question bank
  const [availableQuestions, setAvailableQuestions] = useState<Question[]>([]);
  const [questionFilter, setQuestionFilter] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Load wizard if editing
  useEffect(() => {
    if (open && wizardId) {
      loadWizard();
    } else if (open && !wizardId) {
      // Reset for new wizard
      resetForm();
    }
  }, [open, wizardId]);

  // Load questions
  useEffect(() => {
    if (open) {
      loadQuestions();
    }
  }, [open]);

  async function loadWizard() {
    setLoading(true);
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/wizard-templates/${wizardId}`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'x-consulting-firm-id': consultingFirmId
          }
        }
      );

      if (!response.ok) throw new Error('Failed to load wizard');

      const data = await response.json();
      const wizard = data.wizard;

      setName(wizard.name);
      setDescription(wizard.description);
      setCategory(wizard.category);
      setPages(wizard.pages.map((p: any) => ({
        id: p.id,
        title: p.title,
        description: p.description || '',
        questionIds: p.questionIds
      })));
      setAllowSkip(wizard.allowSkip);
      setShowProgress(wizard.showProgress);
      setAutoSave(wizard.autoSave);
      setPublished(wizard.published);
      setPlacement(wizard.placement);
    } catch (error) {
      console.error('Error loading wizard:', error);
      toast.error('Failed to load wizard template');
    } finally {
      setLoading(false);
    }
  }

  async function loadQuestions() {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/question-bank/${consultingFirmId}/questions`,
        {
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'x-consulting-firm-id': consultingFirmId
          }
        }
      );

      if (!response.ok) throw new Error('Failed to load questions');

      const data = await response.json();
      setAvailableQuestions(Object.values(data.questions));
    } catch (error) {
      console.error('Error loading questions:', error);
      toast.error('Failed to load question bank');
    }
  }

  function resetForm() {
    setName('');
    setDescription('');
    setCategory('service_requests');
    setPages([{ id: 'page_1', title: 'Page 1', description: '', questionIds: [] }]);
    setAllowSkip(true);
    setShowProgress(true);
    setAutoSave(true);
    setPublished(false);
    setPlacement({
      autoLaunch: {
        enabled: false,
        trigger: 'never',
        categories: []
      },
      manualAccess: {
        showInCategoryActions: true,
        showInGridActions: true,
        showInCIDetail: true,
        showInWizardLibrary: true
      },
      availableToRoles: ['both'],
      requiresConfigItem: true,
      allowMultipleRuns: true
    });
    setActiveTab('pages');
  }

  async function handleSave() {
    // Validation
    if (!name.trim()) {
      toast.error('Please enter a wizard name');
      return;
    }

    if (pages.length === 0) {
      toast.error('Wizard must have at least one page');
      return;
    }

    const emptyPages = pages.filter(p => p.questionIds.length === 0);
    if (emptyPages.length > 0) {
      toast.error('All pages must have at least one question');
      return;
    }

    setSaving(true);
    try {
      const wizardData = {
        name,
        description,
        category,
        pages: pages.map(p => ({
          id: p.id,
          title: p.title,
          description: p.description,
          questionIds: p.questionIds
        })),
        allowSkip,
        showProgress,
        autoSave,
        placement
      };

      const url = wizardId
        ? `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/wizard-templates/${wizardId}`
        : `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/wizard-templates`;

      const response = await fetch(url, {
        method: wizardId ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'x-consulting-firm-id': consultingFirmId,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(wizardData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to save wizard');
      }

      const result = await response.json();
      
      toast.success(wizardId ? 'Wizard updated successfully' : 'Wizard created successfully');
      
      if (onSave) {
        onSave(result.wizard);
      }
      
      onClose();
    } catch (error: any) {
      console.error('Error saving wizard:', error);
      toast.error(error.message || 'Failed to save wizard template');
    } finally {
      setSaving(false);
    }
  }

  function addPage() {
    const newPage: WizardPage = {
      id: `page_${pages.length + 1}`,
      title: `Page ${pages.length + 1}`,
      description: '',
      questionIds: []
    };
    setPages([...pages, newPage]);
  }

  function removePage(pageIndex: number) {
    if (pages.length <= 1) {
      toast.error('Wizard must have at least one page');
      return;
    }
    setPages(pages.filter((_, i) => i !== pageIndex));
  }

  function updatePage(pageIndex: number, updates: Partial<WizardPage>) {
    const updatedPages = [...pages];
    updatedPages[pageIndex] = { ...updatedPages[pageIndex], ...updates };
    setPages(updatedPages);
  }

  function addQuestionToPage(pageIndex: number, questionId: string) {
    const page = pages[pageIndex];
    if (page.questionIds.includes(questionId)) {
      toast.error('Question already added to this page');
      return;
    }
    updatePage(pageIndex, {
      questionIds: [...page.questionIds, questionId]
    });
  }

  function removeQuestionFromPage(pageIndex: number, questionId: string) {
    const page = pages[pageIndex];
    updatePage(pageIndex, {
      questionIds: page.questionIds.filter(id => id !== questionId)
    });
  }

  function movePageUp(pageIndex: number) {
    if (pageIndex === 0) return;
    const newPages = [...pages];
    [newPages[pageIndex - 1], newPages[pageIndex]] = [newPages[pageIndex], newPages[pageIndex - 1]];
    setPages(newPages);
  }

  function movePageDown(pageIndex: number) {
    if (pageIndex === pages.length - 1) return;
    const newPages = [...pages];
    [newPages[pageIndex], newPages[pageIndex + 1]] = [newPages[pageIndex + 1], newPages[pageIndex]];
    setPages(newPages);
  }

  const filteredQuestions = availableQuestions.filter(q => {
    if (questionFilter && !q.text.toLowerCase().includes(questionFilter.toLowerCase())) {
      return false;
    }
    if (selectedCategory !== 'all' && q.category !== selectedCategory) {
      return false;
    }
    return true;
  });

  const categories = ['all', ...new Set(availableQuestions.map(q => q.category))];

  if (loading) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-6xl h-[80vh]">
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading wizard...</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-7xl h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-2xl">
                {wizardId ? 'Edit Wizard Template' : 'Create Wizard Template'}
              </DialogTitle>
              <p className="text-sm text-gray-500 mt-1">
                Build custom guided flows for your clients
              </p>
            </div>
            <div className="flex items-center gap-2">
              {wizardId && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPublished(!published)}
                >
                  {published ? <Eye className="w-4 h-4 mr-2" /> : <EyeOff className="w-4 h-4 mr-2" />}
                  {published ? 'Published' : 'Unpublished'}
                </Button>
              )}
              <Button
                onClick={handleSave}
                disabled={saving}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                <Save className="w-4 h-4 mr-2" />
                {saving ? 'Saving...' : 'Save Wizard'}
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Tabs */}
        <div className="border-b px-6">
          <div className="flex gap-6">
            <button
              onClick={() => setActiveTab('pages')}
              className={`py-3 border-b-2 transition-colors ${
                activeTab === 'pages'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Layout className="w-4 h-4 inline mr-2" />
              Pages & Questions
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`py-3 border-b-2 transition-colors ${
                activeTab === 'settings'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Settings className="w-4 h-4 inline mr-2" />
              Settings
            </button>
            <button
              onClick={() => setActiveTab('placement')}
              className={`py-3 border-b-2 transition-colors ${
                activeTab === 'placement'
                  ? 'border-indigo-600 text-indigo-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              <Zap className="w-4 h-4 inline mr-2" />
              Placement & Launch
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          {activeTab === 'pages' && (
            <PagesTab
              name={name}
              setName={setName}
              description={description}
              setDescription={setDescription}
              category={category}
              setCategory={setCategory}
              pages={pages}
              addPage={addPage}
              removePage={removePage}
              updatePage={updatePage}
              movePageUp={movePageUp}
              movePageDown={movePageDown}
              filteredQuestions={filteredQuestions}
              questionFilter={questionFilter}
              setQuestionFilter={setQuestionFilter}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              categories={categories}
              addQuestionToPage={addQuestionToPage}
              removeQuestionFromPage={removeQuestionFromPage}
              consultingFirmId={consultingFirmId}
              accessToken={accessToken}
              onQuestionsReload={loadQuestions}
            />
          )}

          {activeTab === 'settings' && (
            <SettingsTab
              allowSkip={allowSkip}
              setAllowSkip={setAllowSkip}
              showProgress={showProgress}
              setShowProgress={setShowProgress}
              autoSave={autoSave}
              setAutoSave={setAutoSave}
            />
          )}

          {activeTab === 'placement' && (
            <PlacementTab
              placement={placement}
              setPlacement={setPlacement}
              category={category}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Pages Tab Component
function PagesTab({
  name, setName, description, setDescription, category, setCategory,
  pages, addPage, removePage, updatePage, movePageUp, movePageDown,
  filteredQuestions, questionFilter, setQuestionFilter,
  selectedCategory, setSelectedCategory, categories,
  addQuestionToPage, removeQuestionFromPage,
  consultingFirmId, accessToken, onQuestionsReload
}: any) {
  const [expandedPage, setExpandedPage] = useState<number>(0);
  const [showCreateQuestion, setShowCreateQuestion] = useState(false);
  const [availableQuestions, setAvailableQuestions] = useState(filteredQuestions);

  // Keep local state in sync with parent
  useEffect(() => {
    setAvailableQuestions(filteredQuestions);
  }, [filteredQuestions]);

  const handleCreateQuestion = async (questionData: Partial<QuestionBankQuestion>) => {
    try {
      // Call the API to create the question
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/question-bank/${consultingFirmId}/questions`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'x-consulting-firm-id': consultingFirmId,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            question: questionData
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to create question');
      }

      const result = await response.json();
      
      // Close the dialog
      setShowCreateQuestion(false);
      
      // Show success message
      toast.success('Question created', {
        description: 'The question has been added to your question bank and is now available in the wizard.'
      });
      
      // Reload the question list from the parent
      if (onQuestionsReload) {
        await onQuestionsReload();
      }
    } catch (error) {
      console.error('Error creating question:', error);
      toast.error('Failed to create question. Please try again.');
    }
  };

  return (
    <div className="grid grid-cols-[1fr_350px] h-full overflow-hidden">
      {/* Left: Wizard configuration */}
      <div className="overflow-y-auto p-6 border-r h-full">
        <div className="max-w-3xl space-y-6">
          {/* Basic info */}
          <div>
            <Label htmlFor="wizard-name">Wizard Name *</Label>
            <Input
              id="wizard-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Request Type Builder"
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="wizard-description">Description</Label>
            <Textarea
              id="wizard-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What does this wizard help users configure?"
              className="mt-1"
              rows={2}
            />
          </div>

          <div>
            <Label htmlFor="wizard-category">Category *</Label>
            <select
              id="wizard-category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full mt-1 px-3 py-2 border rounded-md"
            >
              <option value="service_requests">Service Requests</option>
              <option value="incidents">Incidents</option>
              <option value="assets">Assets</option>
              <option value="workflows">Workflows</option>
              <option value="slas">SLAs</option>
              <option value="queues">Queues</option>
              <option value="knowledge">Knowledge Base</option>
              <option value="other">Other</option>
            </select>
          </div>

          {/* Pages */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">Pages</h3>
              <Button onClick={addPage} size="sm" variant="outline">
                <Plus className="w-4 h-4 mr-1" />
                Add Page
              </Button>
            </div>

            <div className="space-y-3">
              {pages.map((page: WizardPage, pageIndex: number) => (
                <div key={page.id} className="border rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex-1">
                      <Input
                        value={page.title}
                        onChange={(e) => updatePage(pageIndex, { title: e.target.value })}
                        placeholder="Page title"
                        className="font-medium mb-2"
                      />
                      <Input
                        value={page.description}
                        onChange={(e) => updatePage(pageIndex, { description: e.target.value })}
                        placeholder="Page description (optional)"
                        className="text-sm"
                      />
                    </div>
                    <div className="flex items-center gap-1 ml-2">
                      <button
                        onClick={() => movePageUp(pageIndex)}
                        disabled={pageIndex === 0}
                        className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
                      >
                        <ChevronUp className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => movePageDown(pageIndex)}
                        disabled={pageIndex === pages.length - 1}
                        className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => removePage(pageIndex)}
                        className="p-1 hover:bg-red-100 text-red-600 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Questions in this page */}
                  <div className="mt-3 pt-3 border-t">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm text-gray-600">
                        {page.questionIds.length} question{page.questionIds.length !== 1 ? 's' : ''}
                      </span>
                      <button
                        onClick={() => setExpandedPage(expandedPage === pageIndex ? -1 : pageIndex)}
                        className="text-sm text-indigo-600 hover:text-indigo-700"
                      >
                        {expandedPage === pageIndex ? 'Collapse' : 'Expand'}
                      </button>
                    </div>

                    {expandedPage === pageIndex && (
                      <div className="space-y-2 mt-2">
                        {page.questionIds.map((qId: string) => {
                          const question = filteredQuestions.find((q: Question) => q.id === qId);
                          if (!question) return null;

                          return (
                            <div
                              key={qId}
                              className="flex items-start justify-between p-2 bg-gray-50 rounded text-sm"
                            >
                              <div className="flex-1">
                                <p className="font-medium">{question.text}</p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {question.type} • {question.category}
                                  {question.required && ' • Required'}
                                </p>
                              </div>
                              <button
                                onClick={() => removeQuestionFromPage(pageIndex, qId)}
                                className="ml-2 p-1 hover:bg-red-100 text-red-600 rounded"
                              >
                                <X className="w-3 h-3" />
                              </button>
                            </div>
                          );
                        })}

                        {page.questionIds.length === 0 && (
                          <p className="text-sm text-gray-500 text-center py-4">
                            Drag questions from the right panel
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Right: Question Bank */}
      <div className="flex flex-col h-full overflow-hidden">
        {/* Fixed header */}
        <div className="p-4 border-b bg-gray-50">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-sm">Question Bank</h3>
            <Button
              onClick={() => setShowCreateQuestion(true)}
              size="sm"
              variant="default"
              className="bg-indigo-600 hover:bg-indigo-700"
            >
              <Sparkles className="w-3 h-3 mr-1" />
              Create
            </Button>
          </div>
          <Input
            placeholder="Search questions..."
            value={questionFilter}
            onChange={(e) => setQuestionFilter(e.target.value)}
            className="mb-2"
          />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="w-full px-3 py-2 border rounded-md text-sm"
          >
            <option value="">All Categories</option>
            {categories.map((cat: string) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        {/* Scrollable questions list */}
        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-2">
            {filteredQuestions.length === 0 && (
              <div className="text-center py-8">
                <p className="text-sm text-gray-500 mb-3">
                  No questions found
                </p>
                <Button
                  onClick={() => setShowCreateQuestion(true)}
                  size="sm"
                  variant="outline"
                  className="border-indigo-300 text-indigo-600 hover:bg-indigo-50"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create your first question
                </Button>
              </div>
            )}

            {filteredQuestions.map((question: Question) => (
              <div
                key={question.id}
                className="p-3 bg-white border rounded-lg hover:border-indigo-300 cursor-pointer transition-colors"
                onClick={() => {
                  if (expandedPage >= 0) {
                    addQuestionToPage(expandedPage, question.id);
                  } else {
                    toast.error('Please expand a page first');
                  }
                }}
              >
                <p className="text-sm font-medium mb-1">{question.text}</p>
                <div className="flex items-center gap-2 text-xs text-gray-500">
                  <span className="px-1.5 py-0.5 bg-gray-100 rounded">{question.type}</span>
                  <span>{question.category}</span>
                  {question.required && <span className="text-orange-600">Required</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Question Editor Dialog */}
        {showCreateQuestion && (
          <Dialog open={showCreateQuestion} onOpenChange={setShowCreateQuestion}>
            <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto p-0">
              <QuestionEditor
                firmId={''} // Will be populated from parent context
                onSave={handleCreateQuestion}
                onCancel={() => setShowCreateQuestion(false)}
              />
            </DialogContent>
          </Dialog>
        )}
      </div>
    </div>
  );
}

// Settings Tab Component
function SettingsTab({ allowSkip, setAllowSkip, showProgress, setShowProgress, autoSave, setAutoSave }: any) {
  return (
    <div className="p-6 overflow-y-auto">
      <div className="max-w-2xl space-y-6">
        <div>
          <h3 className="font-semibold mb-4">Wizard Behavior</h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">Allow Skip Questions</p>
                <p className="text-sm text-gray-500">Users can skip non-required questions</p>
              </div>
              <Switch checked={allowSkip} onCheckedChange={setAllowSkip} />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">Show Progress Bar</p>
                <p className="text-sm text-gray-500">Display progress indicator at top of wizard</p>
              </div>
              <Switch checked={showProgress} onCheckedChange={setShowProgress} />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">Auto-Save Answers</p>
                <p className="text-sm text-gray-500">Save answers automatically as user types</p>
              </div>
              <Switch checked={autoSave} onCheckedChange={setAutoSave} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Placement Tab Component
function PlacementTab({ placement, setPlacement, category }: any) {
  return (
    <div className="p-6 overflow-y-auto">
      <div className="max-w-2xl space-y-6">
        {/* Auto-Launch */}
        <div>
          <h3 className="font-semibold mb-4">Auto-Launch Settings</h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">Enable Auto-Launch</p>
                <p className="text-sm text-gray-500">Automatically show wizard in certain scenarios</p>
              </div>
              <Switch
                checked={placement.autoLaunch.enabled}
                onCheckedChange={(enabled) =>
                  setPlacement({
                    ...placement,
                    autoLaunch: { ...placement.autoLaunch, enabled }
                  })
                }
              />
            </div>

            {placement.autoLaunch.enabled && (
              <>
                <div className="p-4 border rounded-lg">
                  <Label className="mb-2 block">Trigger</Label>
                  <select
                    value={placement.autoLaunch.trigger}
                    onChange={(e) =>
                      setPlacement({
                        ...placement,
                        autoLaunch: {
                          ...placement.autoLaunch,
                          trigger: e.target.value
                        }
                      })
                    }
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="never">Never</option>
                    <option value="on_create">When config item created</option>
                    <option value="on_first_view">On first view (no answers yet)</option>
                  </select>
                </div>

                <div className="p-4 border rounded-lg">
                  <Label className="mb-2 block">Auto-Launch Categories</Label>
                  <p className="text-sm text-gray-500 mb-2">
                    Wizard will auto-launch for these config item categories
                  </p>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={placement.autoLaunch.categories.includes(category)}
                      onChange={(e) => {
                        const categories = e.target.checked
                          ? [...placement.autoLaunch.categories, category]
                          : placement.autoLaunch.categories.filter((c: string) => c !== category);
                        setPlacement({
                          ...placement,
                          autoLaunch: { ...placement.autoLaunch, categories }
                        });
                      }}
                    />
                    <span>{category}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Manual Access */}
        <div>
          <h3 className="font-semibold mb-4">Manual Launch Points</h3>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">Show in Category Actions</p>
                <p className="text-sm text-gray-500">Available from category card menu</p>
              </div>
              <Switch
                checked={placement.manualAccess.showInCategoryActions}
                onCheckedChange={(checked) =>
                  setPlacement({
                    ...placement,
                    manualAccess: { ...placement.manualAccess, showInCategoryActions: checked }
                  })
                }
              />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">Show in Grid Actions</p>
                <p className="text-sm text-gray-500">Available from config item grid</p>
              </div>
              <Switch
                checked={placement.manualAccess.showInGridActions}
                onCheckedChange={(checked) =>
                  setPlacement({
                    ...placement,
                    manualAccess: { ...placement.manualAccess, showInGridActions: checked }
                  })
                }
              />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">Show in Config Item Detail</p>
                <p className="text-sm text-gray-500">Available from config item detail page</p>
              </div>
              <Switch
                checked={placement.manualAccess.showInCIDetail}
                onCheckedChange={(checked) =>
                  setPlacement({
                    ...placement,
                    manualAccess: { ...placement.manualAccess, showInCIDetail: checked }
                  })
                }
              />
            </div>

            <div className="flex items-center justify-between p-4 border rounded-lg">
              <div>
                <p className="font-medium">Show in Wizard Library</p>
                <p className="text-sm text-gray-500">Browsable in wizard library</p>
              </div>
              <Switch
                checked={placement.manualAccess.showInWizardLibrary}
                onCheckedChange={(checked) =>
                  setPlacement({
                    ...placement,
                    manualAccess: { ...placement.manualAccess, showInWizardLibrary: checked }
                  })
                }
              />
            </div>
          </div>
        </div>

        {/* Role Visibility */}
        <div>
          <h3 className="font-semibold mb-4">Role Visibility</h3>
          
          <div className="p-4 border rounded-lg">
            <Label className="mb-2 block">Available To</Label>
            <select
              value={placement.availableToRoles[0]}
              onChange={(e) =>
                setPlacement({
                  ...placement,
                  availableToRoles: [e.target.value]
                })
              }
              className="w-full px-3 py-2 border rounded-md"
            >
              <option value="both">Clients & Consultants</option>
              <option value="client">Clients Only</option>
              <option value="consultant">Consultants Only</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}